import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useModalManager } from './ModalProvider';

interface ModalShellProps {
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  panelClassName?: string;
  closeOnOverlayClick?: boolean;
  ariaLabel?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getModalRoot = () => {
  if (typeof document === 'undefined') return null;
  return document.getElementById('modal-root') ?? document.body;
};

export const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  onClose,
  children,
  panelClassName = '',
  closeOnOverlayClick = false,
  ariaLabel = 'Dialog',
}) => {
  const { registerModal } = useModalManager();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const modalRoot = useMemo(getModalRoot, []);

  useEffect(() => {
    if (!isOpen) return;
    registerModal(true);
    return () => registerModal(false);
  }, [isOpen, registerModal]);

  useEffect(() => {
    if (!isOpen) return;
    const previousActive = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusable = panel?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const first = focusable?.[0] ?? panel;
    first?.focus();
    return () => {
      previousActive?.focus();
    };
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      onClose?.();
      return;
    }
    if (event.key !== 'Tab') return;

    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const isShift = event.shiftKey;
    const active = document.activeElement as HTMLElement | null;

    if (isShift && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!isShift && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen || !modalRoot) return null;

  return createPortal(
    <div
      className="modal-overlay p-4"
      onClick={(event) => {
        if (!closeOnOverlayClick) return;
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`modal-panel ${panelClassName}`}
      >
        {children}
      </div>
    </div>,
    modalRoot
  );
};
